// Keyboard input — capture key state into viewer.input.keys (setupListeners)
// and the per-frame state machine driven from the animate loop (handleInput).
//
// Edge-triggered UI actions (panel toggle/cycle) are dispatched as intents on
// the shared store; the UI panels driver in src/ui/panels.ts owns the DOM.
// Camera + selection-cube movement still mutates viewer + params directly
// (those are scene state, not UI).

import { threeJSToPhysical } from '@adapters/coordinates';
import { updateSelectedRegion as selUpdateSelectedRegion } from '@scene/selection';
import { setSelectionSize as selSetSelectionSize } from '@scene/picking';
import { buildRegionContext } from '@scene/regionContext';
import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import { getActiveController } from '@app/cameraModes';
import { debugLog } from '@adapters/debug';
import type { ViewerContext } from '@app/context';
import type { ParamsState } from '@store/schema';

/** Wire the document-level keydown/keyup listeners that populate viewer.input.keys. */
export function setupListeners(viewer: ViewerContext): void {
    document.addEventListener('keydown', (event) => {
        const active = document.activeElement as (HTMLElement | null);
        const tag = (active && active.tagName) || '';
        const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (active && active.isContentEditable);
        if ((event.code === 'ArrowUp' || event.code === 'ArrowDown' || event.code === 'ArrowLeft' || event.code === 'ArrowRight') && !isTyping) {
            event.preventDefault();
            event.stopPropagation();
        }
        viewer.input.keys[event.code] = true;
        debugLog(viewer, 'Key pressed:', event.code);
    }, true);

    document.addEventListener('keyup', (event) => {
        viewer.input.keys[event.code] = false;
        if (event.code === 'Comma') viewer.input.keys['Comma_processed'] = false;
        if (event.code === 'Period') viewer.input.keys['Period_processed'] = false;
        if (event.code === 'Semicolon') viewer.input.keys['Semicolon_processed'] = false;
        if (event.code === 'KeyG') {
            viewer.input.keys['KeyG_processed'] = false;
            viewer.input.keys['KeyG_shift_processed'] = false;
        }
        if (event.code === 'KeyU') viewer.input.keys['KeyU_processed'] = false;
        if (event.code === 'KeyC') viewer.input.keys['KeyC_processed'] = false;
        if (event.code === 'KeyF') viewer.input.keys['KeyF_processed'] = false;
        if (event.code === 'Quote') viewer.input.keys['Quote_processed'] = false;
    }, true);
}

const CUBE_SPEED = 0.1;
const SIZE_SPEED = 0.05;

interface HandleInputFn {
    (viewer: ViewerContext, params: ParamsState, dt: number): void;
    _throttle?: number;
}

export const handleInput: HandleInputFn = (viewer: ViewerContext, params: ParamsState, dt: number): void => {
    // Suppress camera/selection movement while focus is in a text input,
    // textarea, select, or contentEditable — otherwise arrow keys both edit
    // the field and slide the cube/camera.
    const active = document.activeElement as (HTMLElement | null);
    const tag = (active && active.tagName) || '';
    const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (active && active.isContentEditable);
    if (isTyping) return;

    const keys = viewer.input.keys;

    const clampAndSyncSelection = (): void => {
        const inv = viewer.objects.selection.invisibleMesh;
        if (!inv) return;
        const phys = threeJSToPhysical(inv.position.x, inv.position.y, inv.position.z);
        store.dispatch(INTENTS.MOVE_SELECTION, { x: phys.x, y: phys.y, z: phys.z });
        store.dispatch(INTENTS.UI_PANE_REFRESH);
    };

    if (keys['Comma'] && !keys['Comma_processed']) {
        store.dispatch(INTENTS.UI_PANEL_TOGGLE, { which: 'controls' });
        keys['Comma_processed'] = true;
    }

    if (keys['KeyF'] && !keys['KeyF_processed']) {
        store.dispatch(INTENTS.UI_PANEL_CYCLE);
        keys['KeyF_processed'] = true;
    }

    if (keys['Quote'] && !keys['Quote_processed']) {
        store.dispatch(INTENTS.CYCLE_THEME);
        keys['Quote_processed'] = true;
    }

    // Camera movement is owned by the active navigation-mode controller
    // (orbit / turntable / fly). dt-scaled so motion is frame-rate independent.
    getActiveController().handleKeyboard(viewer, dt);

    if (keys['KeyG'] && !keys['KeyG_processed']) {
        store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'XZ', value: !params.showGridXZ });
        store.dispatch(INTENTS.UI_PANE_REFRESH);
        keys['KeyG_processed'] = true;
    }

    if ((keys['ShiftLeft'] || keys['ShiftRight']) && keys['KeyG'] && !keys['KeyG_shift_processed']) {
        store.dispatch(INTENTS.SET_SHOW_GRID, { plane: 'XY', value: !params.showGridXY });
        store.dispatch(INTENTS.UI_PANE_REFRESH);
        keys['KeyG_shift_processed'] = true;
    }

    if (keys['KeyU'] && !keys['KeyU_processed']) {
        keys['KeyU_processed'] = true;
    }

    const inv = viewer.objects.selection.invisibleMesh;
    if (!inv) return;

    if (keys['ShiftLeft'] || keys['ShiftRight']) {
        if (keys['ArrowUp']) {
            inv.position.y += CUBE_SPEED;
            if (!handleInput._throttle || performance.now() - handleInput._throttle > 16) {
                handleInput._throttle = performance.now();
                clampAndSyncSelection();
            }
        }
        if (keys['ArrowDown']) {
            inv.position.y -= CUBE_SPEED;
            if (!handleInput._throttle || performance.now() - handleInput._throttle > 16) {
                handleInput._throttle = performance.now();
                clampAndSyncSelection();
            }
        }
        if (keys['ArrowLeft']) {
            viewer.objects.selection.size = Math.max(0.1, viewer.objects.selection.size - SIZE_SPEED);
            debugLog(viewer, 'Cube size decreased');
            selSetSelectionSize(viewer.objects.selection.size);
            selUpdateSelectedRegion(buildRegionContext(viewer, params), inv.position);
        }
        if (keys['ArrowRight']) {
            viewer.objects.selection.size += SIZE_SPEED;
            debugLog(viewer, 'Cube size increased');
            selSetSelectionSize(viewer.objects.selection.size);
            selUpdateSelectedRegion(buildRegionContext(viewer, params), inv.position);
        }
    } else {
        if (keys['ArrowUp']) {
            inv.position.z += CUBE_SPEED;
            if (!handleInput._throttle || performance.now() - handleInput._throttle > 16) {
                handleInput._throttle = performance.now();
                clampAndSyncSelection();
            }
        }
        if (keys['ArrowDown']) {
            inv.position.z -= CUBE_SPEED;
            if (!handleInput._throttle || performance.now() - handleInput._throttle > 16) {
                handleInput._throttle = performance.now();
                clampAndSyncSelection();
            }
        }
        if (keys['ArrowLeft']) {
            inv.position.x += CUBE_SPEED;
            if (!handleInput._throttle || performance.now() - handleInput._throttle > 16) {
                handleInput._throttle = performance.now();
                clampAndSyncSelection();
            }
        }
        if (keys['ArrowRight']) {
            inv.position.x -= CUBE_SPEED;
            if (!handleInput._throttle || performance.now() - handleInput._throttle > 16) {
                handleInput._throttle = performance.now();
                clampAndSyncSelection();
            }
        }
    }
};
